import locale from "@bonliva-crm/locale";
import { Box, InputLabel, makeStyles, FormControl } from "@material-ui/core";
import ReactSelect, {
  GroupBase,
  OptionsOrGroups,
  Props,
  components
} from "react-select5";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";
import { useSelectStyle } from "./hooks/use-select-style";
import { ReactNode, useEffect, useState } from "react";
import { Color } from "../../../theme";
import { RadioOptionComponent } from "./components/radio-option";
import { ClearIndicatorComponent } from "./components/clear-indicator";
import { SelectBadge } from "./components/select-badge";

type SelectProps<Option, Group extends GroupBase<Option> = GroupBase<Option>> = Omit<
  Props<Option, false, Group>,
  "styles"
> & {
  selectRef?: any;
  showBadge?: boolean;
  inputLabel?: string;
  optionType?: "radio" | "default";
  styleProps?: Parameters<typeof useSelectStyle<Option, false, Group>>[0];
  sortOptionsOnMenuOpenFn?: (
    options: OptionsOrGroups<Option, Group>
  ) => OptionsOrGroups<Option, Group>;
  showClearIndicator?: boolean;
  onClear?: () => void;
  onOptionEdit?: (option: Option) => void;
  onOptionDelete?: (option: Option) => void;
  // hideController?: boolean;
  menuWidth?: string;
  children?: ReactNode;
  menuChildrenPosition?: "top" | "bottom";
  error?: boolean;
  labelShrink?: boolean;
};

export function SingleSelect<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
>({ optionType = "default", ...mainProps }: SelectProps<Option, Group>) {
  const { selectStyle, selectTheme } = useSelectStyle<Option, false, Group>(
    mainProps.styleProps
  );
  const classes = useStyles({
    menuWidth: mainProps.menuWidth
  });

  const {
    inputLabel,
    showBadge,
    options: _options,
    components: _components,
    onOptionEdit,
    onOptionDelete,
    children,
    menuChildrenPosition,
    error,
    name,
    selectRef,
    labelShrink,
    onMenuOpen,
    ...rest
  } = mainProps;

  const [options, setOptions] = useState<OptionsOrGroups<Option, Group> | undefined>(
    _options
  );
  const [isOpen, _setIsOpen] = useState(mainProps.menuIsOpen ?? false);

  useEffect(() => {
    setOptions(_options);
  }, [_options]);

  const firstValue =
    mainProps.value &&
    typeof mainProps.value === "object" &&
    "label" in mainProps.value &&
    mainProps.value.label;

  const hasValue = !!firstValue;
  return (
    <Box className={classes.root}>
      {showBadge && (
        <SelectBadge
          selectedAmount={mainProps.value ? 1 : 0}
          text={firstValue ? (firstValue as string) : undefined}
        />
      )}

      <FormControl>
        {inputLabel && (
          <InputLabel
            htmlFor={name}
            error={error}
            shrink={hasValue || isOpen || labelShrink}
            required={rest.required}
            disabled={mainProps.isDisabled}
          >
            {inputLabel}
          </InputLabel>
        )}
        <ReactSelect<Option, false, Group>
          menuIsOpen={isOpen}
          components={{
            Option: (p) =>
              optionType === "radio" ? (
                RadioOptionComponent({
                  ...p,
                  onEdit: onOptionEdit,
                  onDelete: onOptionDelete
                })
              ) : (
                <components.Option {...p} />
              ),
            DropdownIndicator: (p) => DropdownIndicatorComponent(p),
            IndicatorSeparator: null,
            ClearIndicator: (p) =>
              mainProps.isClearable && mainProps.onClear
                ? ClearIndicatorComponent({ ...p, onClear: mainProps.onClear })
                : null,
            ..._components
          }}
          styles={selectStyle}
          theme={(theme) => ({
            ...theme,
            ...selectTheme
          })}
          closeMenuOnSelect={true}
          hideSelectedOptions={true}
          isClearable={false}
          backspaceRemovesValue={true}
          noOptionsMessage={() => locale.NoMoreSearchResults}
          loadingMessage={() => ""}
          isMulti={false}
          isSearchable={true}
          options={options}
          blurInputOnSelect
          onMenuClose={() => _setIsOpen(false)}
          onMenuOpen={() => {
            _setIsOpen(true);
            if (mainProps.sortOptionsOnMenuOpenFn) {
              options && setOptions(mainProps.sortOptionsOnMenuOpenFn(options.slice()));
            }
            onMenuOpen && onMenuOpen();
          }}
          ref={selectRef}
          {...rest}
        />
      </FormControl>
    </Box>
  );
}

type StyleProps = {
  menuWidth?: string;
};

const useStyles = makeStyles((theme) => ({
  root: (props: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    // rowGap: "0.5rem",
    position: "relative",
    width: props.menuWidth
  }),
  badge: {
    position: "absolute",
    left: "calc(100% - 0.75rem)",
    bottom: "calc(100% - 0.75rem)",
    borderRadius: "200rem",
    backgroundColor: Color.DoveBlue,
    border: `1px solid ${Color.DoveBlue}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "1.5rem",
    width: "1.5rem",
    color: Color.White,
    zIndex: 10
  }
}));

export default SingleSelect;
